/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {PackageItem} from "types/entities/PackageItem";
import {GroupedPackageItem} from "types/entities/GroupedPackageItem";
import {translate} from "utils/utility";

export interface GroupNameItem {
    groupName: string;
    items: GroupedPackageItem[];
}
// grouped package items
export const useGetGroupedUpsells = (list: PackageItem[]) => {
    // create grouped upsells and normal upsell
    const groupedItems = list.reduce((acc: GroupedPackageItem[], item): GroupedPackageItem[] => {
        const variant_id = item.variant_id;
        const is_variant = item.is_variant;

        if (item.extra.groupName == "Sonstiges") {
            item.extra.groupName = "";
        }
        // upsell without group
        if (!is_variant) {
            acc.push({
                groupName: item.extra.groupName,
                variant_id: null,
                items: [item],
                image: item.image,
                position: item.position,
                name: item.name,
                description: item.description,
                description_html: item.description_html,
                packageItemVariant: null,
                is_variant: false,
                variant_description: null,
            });
            return acc;
        }

        // find or create a group with this variant_id
        const existingGroup = acc.find(group => group.variant_id === variant_id);
        if (!existingGroup) {
            if (item.extra.groupName == "Sonstiges") {
                item.extra.groupName = "";
            }
            acc.push({
                groupName: item.extra.groupName,
                variant_id: variant_id,
                items: [item],
                image: item.packageItemVariant?.image || item.image,
                // position: item.packageItemVariant?.position || item.position,
                position: item.position,
                name: item.packageItemVariant?.name || item.name,
                description: item.packageItemVariant?.description || item.description,
                description_html: item.packageItemVariant?.description_html || item.description_html,
                packageItemVariant: item.packageItemVariant,
                is_variant: item.is_variant,
                variant_description: translate(item.variant_description),
            });
        } else {
            existingGroup.items.push(item);
            if (!existingGroup.image) {
                existingGroup.image = item.packageItemVariant?.image || item.image;
            }
            if (!existingGroup.position) {
                existingGroup.position = item.position;
            }
            if (!existingGroup.name) {
                existingGroup.name = item.packageItemVariant?.name || item.name;
            }
            if (!existingGroup.description) {
                existingGroup.description = item.packageItemVariant?.description || item.description;
            }
            if (!existingGroup.description_html) {
                existingGroup.description_html = item.packageItemVariant?.description_html || item.description_html;
            }
        }

        return acc;
    }, []);

    // sort groups by their position
    const groupedUpsellsAll = groupedItems.sort((a: GroupedPackageItem, b: GroupedPackageItem) => {
        if (a.position === null && b.position !== null) return 1;
        if (b.position === null && a.position !== null) return -1;
        if (a.position === null && b.position === null) return 0;
        return a.position! - b.position!;
    });

    const mappedByGroupNames = groupedUpsellsAll
        .flatMap((item: GroupedPackageItem) => {
            const groupKey = !item.groupName ? "favorites" : item.groupName;
            return [{groupKey, item}];
        })
        .reduce((acc: {[key: string]: GroupedPackageItem[]}, {groupKey, item}) => {
            if (!acc[groupKey]) {
                acc[groupKey] = [];
            }
            acc[groupKey].push(item);
            return acc;
        }, {});
    const groupOrder = ["favorites", "Apero und Snacks", "Canapé Selection", "Party Pakete", "Warmes Essen", "Sonstiges"];

    const sortedByGroupNames: GroupNameItem[] = Object.entries(mappedByGroupNames)
        .map(([groupName, items]) => ({
            groupName,
            items,
        }))
        .sort((a, b) => {
            const aIndex = groupOrder.indexOf(a.groupName);
            const bIndex = groupOrder.indexOf(b.groupName);

            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;

            return aIndex - bIndex;
        });

    return {
        groupedUpsellsAll,
        sortedByGroupNames,
    };
};

